import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useRecentActions } from '../contexts/RecentActionsContext';
import { useToast } from '../providers/ToastProvider';
import { submitRetentionRecords } from '../services/patient';

type RetentionInitialStep = 'intro' | 'congratulations';

/**
 * Upload-retention-records flow. The "Review for retention" decision now lives on
 * the PatientReviewRetention page (routed surface), so this hook only owns the
 * records-upload / congratulations modal.
 */
export function useRetentionWorkflow() {
    const queryClient = useQueryClient();
    const { showToast } = useToast();
    const { markActionCompleted } = useRecentActions();

    const [isUploadRetentionModalOpen, setIsUploadRetentionModalOpen] = useState(false);
    const [uploadRetentionPatientId, setUploadRetentionPatientId] = useState<string | null>(null);
    const [retentionInitialStep, setRetentionInitialStep] = useState<RetentionInitialStep>('intro');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const openUploadRetention = useCallback(
        (patientId: string, initialStep: RetentionInitialStep = 'intro') => {
            setUploadRetentionPatientId(patientId);
            setRetentionInitialStep(initialStep);
            setIsUploadRetentionModalOpen(true);
        },
        []
    );

    const closeUploadRetention = useCallback(() => {
        setIsUploadRetentionModalOpen(false);
        setUploadRetentionPatientId(null);
        setRetentionInitialStep('intro');
    }, []);

    const handleComplete = useCallback(async (): Promise<boolean> => {
        if (!uploadRetentionPatientId) return false;

        setIsSubmitting(true);
        try {
            await submitRetentionRecords(uploadRetentionPatientId);
            queryClient.invalidateQueries({ queryKey: ['patients'] });
            queryClient.invalidateQueries({ queryKey: ['patient', uploadRetentionPatientId] });
            markActionCompleted(uploadRetentionPatientId, 'Upload retention records');
            showToast('Retention records submitted.', 'success');
            return true;
        } catch (error) {
            showToast('Failed to submit retention records.', 'error');
            return false;
        } finally {
            setIsSubmitting(false);
        }
    }, [uploadRetentionPatientId, queryClient, showToast, markActionCompleted]);

    return {
        isUploadRetentionModalOpen,
        uploadRetentionPatientId,
        retentionInitialStep,
        isSubmitting,
        openUploadRetention,
        closeUploadRetention,
        handleComplete
    };
}
